import { Button, List } from '@melements/front-components';
import { NotificationsContext, NotificationTypes } from '@melements/front-components/dist/context';
import { FormApi } from 'final-form';
import React, { Fragment, useContext, useState } from 'react';
import { Loader, LocaleContext, ModalForm, PageHeader } from 'shared';
import { getErrorMessage } from 'utils/getErrorMessage/getErrorMessage';

import { useQuery } from '../../../shared/hooks';
import { Blockade, CreateBlockadePayload } from '../../../store/api/blockades/blockades.types';
import { ActionButtonWrapper, PageWrapper } from '../Dashboard.styles';

import { EndpointsWrapper } from './Endpoints.styles';
import { EndpointsProps } from './Endpoints.types';
import { BankEnvironment } from './form/fields/bankenvironment/BankEnvironment';
import { EndpointList } from './form/fields/endpointlist/EndpointList';
import { PeriodPicker } from './form/fields/periodpicker/PeriodPicker';
import { COLUMNS } from './table/Table.columns';

export const Endpoints = ({ onFetchBlockades, onFetchEndpoints, onCreateBlockade }: EndpointsProps) => {
  const localeContext = useContext(LocaleContext);
  const notificationsContext = useContext(NotificationsContext);
  const {
    response: blockadesResponse,
    error: blockadesError,
    loading: blockadesLoading,
    handleFetch: blockadesHandleFetch,
  } = useQuery(onFetchBlockades);
  const { response: endpointsResponse, error: endpointsError, loading: endpointsLoading } = useQuery(onFetchEndpoints);
  const [addNewLockModalVisible, setAddNewLockModalVisible] = useState(false);
  const [selectedBankEnvironment, setSelectedBankEnvironment] = useState('');

  const handleAddNewLockClick = () => {
    setAddNewLockModalVisible(true);
    setSelectedBankEnvironment(endpointsResponse ? Object.keys(endpointsResponse.payload)[0] : '');
  };

  const selectBankEnvironment = (bankEnvironment: string) => {
    setSelectedBankEnvironment(bankEnvironment);
  };

  const handleSubmit = async (values: CreateBlockadePayload, form: FormApi) => {
    try {
      editFormValuesBeforeSubmit(values);

      return await onCreateBlockade(values, form, () => {
        setAddNewLockModalVisible(false);

        showNotificationSuccess();
        blockadesHandleFetch();
        form.reset();
      });
    } catch (error) {
      showNotificationError(error);
    }
  };

  const editFormValuesBeforeSubmit = (values: CreateBlockadePayload) => {
    if (values && values.period && values.endpoints && values.period.startDateTime && values.period.endDateTime) {
      if (values.period.startDateTime >= values.period.endDateTime) {
        throw new Error(localeContext.formatMessage({ id: 'ENDPOINTS.FORM.ADD_NEW_LOCK_START_DATE_BEFORE_END' }));
      }

      values.endpoints = transformEndpointsMapToArray(values.endpoints);
      if (!values.bankId) {
        values.bankId = selectedBankEnvironment;
      }
    } else {
      throw new Error(localeContext.formatMessage({ id: 'ENDPOINTS.FORM.ADD_NEW_LOCK_EMPTY_FORM_FIELDS' }));
    }
  };

  const transformEndpointsMapToArray = (endpointsMap: any): string[] => {
    let endpointsArray: string[] = [];
    if (endpointsResponse) {
      endpointsArray = Object.entries(endpointsMap)
        .filter(value => value[1])
        .map(value => value[0])
        .filter(value => endpointsResponse.payload[selectedBankEnvironment].includes(value));
      if (endpointsArray.length === 0) {
        throw new Error(localeContext.formatMessage({ id: 'ENDPOINTS.FORM.ADD_NEW_LOCK_NO_ENDPOINTS' }));
      }
    }

    return endpointsArray;
  };

  const showNotificationSuccess = () => {
    notificationsContext.showNotification({
      message: localeContext.formatMessage({ id: 'ENDPOINTS.FORM.ADD_NEW_LOCK-SUCCESS' }),
      time: 3000,
      type: NotificationTypes.SUCCESS,
    });
  };

  const showNotificationError = (error: any) => {
    const errorMessage = error.message ? error.message : getErrorMessage(error, localeContext);
    notificationsContext.showNotification({
      message: errorMessage,
      time: 0,
      type: NotificationTypes.ERROR,
    });
  };

  const formatDataForTable = (blockadeList: Blockade[]) => {
    return blockadeList.map(blockade => {
      return {
        action: blockadesHandleFetch,
        bankId: blockade.bankId,
        endDateTime: formatDateTime(blockade.endDateTime),
        endpoint: blockade.endpoint,
        id: blockade.id,
        startDateTime: formatDateTime(blockade.startDateTime),
      };
    });
  };

  const formatDateTime = (dateTimeString: string) => {
    return new Date(dateTimeString).toLocaleString('pl');
  };

  return (
    <PageWrapper>
      <PageHeader>
        {localeContext.formatMessage({ id: 'DASHBOARD.HEADER.ENDPOINTS-MANAGEMENT' })}
        {endpointsResponse && endpointsResponse.payload && (
          <ActionButtonWrapper>
            <Button type="leading" className="button--action" onClick={handleAddNewLockClick}>
              {localeContext.formatMessage({ id: 'ENDPOINTS.BUTTON.ADD_NEW_LOCK' })}
            </Button>
          </ActionButtonWrapper>
        )}
      </PageHeader>
      {(blockadesLoading || endpointsLoading) && <Loader />}
      {blockadesResponse && blockadesResponse.payload && !blockadesError && (
        <EndpointsWrapper className="endpoints">
          <List
            columns={COLUMNS}
            data={formatDataForTable(blockadesResponse.payload)}
            placeholder={<Fragment>There are no active endpoint blockades</Fragment>}
            loading={false}
          />
        </EndpointsWrapper>
      )}
      {endpointsResponse && endpointsResponse.payload && !endpointsError && (
        <ModalForm
          className="add-new-lock-form"
          name={localeContext.formatMessage({ id: 'ENDPOINTS.FORM.ADD_NEW_LOCK.NAME' })}
          submitName={localeContext.formatMessage({ id: 'GENERAL.SAVE' })}
          isOpen={addNewLockModalVisible}
          onClose={setAddNewLockModalVisible.bind(undefined, false)}
          onSubmit={handleSubmit}
        >
          <BankEnvironment
            bankEnvironments={Object.keys(endpointsResponse.payload).map(key => ({
              label: key,
              value: key,
            }))}
            selectBankEnvironment={selectBankEnvironment}
          />
          <EndpointList endpoints={endpointsResponse.payload[selectedBankEnvironment]} />
          <PeriodPicker />
        </ModalForm>
      )}
    </PageWrapper>
  );
};
